import React from 'react';
import { CheckCircle, Bug, Sparkles } from 'lucide-react';

/**
 * Lead Finder Update Modal
 *
 * Shows bug fixes and improvements to users who have submitted at least one Lead Finder job.
 * Uses localStorage to track if the user has seen a specific update version.
 */

// Update version - increment this when adding new updates
export const LEAD_FINDER_UPDATE_VERSION = '2024-12-22-v1';

const LeadFinderUpdateModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}
    onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '32rem',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--color-success) 0%, var(--color-success-hover) 100%)',
          padding: '24px',
          borderRadius: '8px 8px 0 0',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <Sparkles size={28} />
            <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>
              Lead Finder Updates
            </h2>
          </div>
          <p style={{ fontSize: '14px', opacity: 0.9, margin: 0 }}>
            We've fixed some issues to improve your experience
          </p>
        </div>

        {/* Bug Fixes Section */}
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <Bug size={20} color="#DC2626" />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-text-primary)', margin: 0 }}>
                Bug Fixes
              </h3>
            </div>

            {/* Fix Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <FixItem
                title="Custom Lead Count Now Works"
                description="Fixed an issue where selecting a custom number of leads (e.g., 102) would still only extract 100. Your exact lead count is now honored."
              />
              <FixItem
                title="Input Validation for Filters"
                description="Job Titles, Industries, Countries, and States/Regions now only accept values from the predefined list. This prevents job failures caused by invalid filter values."
              />
              <FixItem
                title="'Extract All' Uses Correct Count"
                description="When selecting 'Extract All Leads', the system now correctly uses the total available leads from the preview, not a default limit."
              />
            </div>
          </div>

          {/* Improvements Section */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <CheckCircle size={20} color="#10B981" />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-text-primary)', margin: 0 }}>
                Improvements
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <FixItem
                title="Strict Input Mode"
                description="Autocomplete fields now show 'No matches found' when typing values that don't exist, preventing invalid entries."
              />
            </div>
          </div>

          {/* Close button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={onClose}
              style={{
                background: 'linear-gradient(135deg, var(--color-success) 0%, var(--color-success-hover) 100%)',
                color: 'white',
                padding: '12px 32px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Fix Item Component
const FixItem = ({ title, description }) => (
  <div style={{
    padding: '12px 16px',
    background: 'var(--color-bg-subtle)',
    borderRadius: '6px',
    borderLeft: '3px solid var(--color-success)'
  }}>
    <h4 style={{
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--color-text-primary)',
      marginBottom: '4px',
      margin: '0 0 4px 0'
    }}>
      {title}
    </h4>
    <p style={{
      fontSize: '13px',
      color: 'var(--color-text-muted)',
      lineHeight: '1.5',
      margin: 0
    }}>
      {description}
    </p>
  </div>
);

export default LeadFinderUpdateModal;
